'use strict';

// var toastMessageHelper = require('../../utils/toastMessageHelper');

/**
 * Sanitizes an email input by removing all invalid characters.
 *
 * Allows only alphabets, numbers, and valid email symbols (@ . _ % + -).
 *
 * @param {string} value - The raw email string entered by the user.
 * @returns {string} The sanitized email string with invalid characters removed.
 */
function sanitizeEmail(value) {
    return value.replace(/[^a-zA-Z0-9@._%+-]/g, '');
}

/**
 * Sanitizes a mobile phone number by removing non-numeric characters and enforcing validation rules.
 * - Removes all non-numeric characters
 * - Limits the length to 10 digits
 * - For +91 numbers, removes any leading digits between 0–5
 *
 * @param {string} value - The mobile phone number to sanitize
 * @param {string} [countryCode='+91'] - Selected country code controlling leading-digit rules
 * @returns {string} The sanitized mobile phone number containing only digits (max 10)
 *
 * @example
 * sanitizeMobile("123-456-7890") // returns "23-456-7890" (removes '1' as it's 0-5, then sanitizes)
 * sanitizeMobile("987-654-3210") // returns "9876543210"
 * sanitizeMobile("5551234567") // returns "551234567" (removes leading '5')
 */
function sanitizeMobile(value, countryCode) {
    let maxLength = $('#mobile-number').attr('maxlength');
    let sanitized = value.replace(/[^0-9]/g, '').substring(0, maxLength);

    // If +91 → block starting digits 0–5
    if (countryCode === '+91' && /^[0-5]/.test(sanitized)) {
        sanitized = sanitized.replace(/^[0-5]+/, '');
    }

    return sanitized;
}

/**
 * Sanitizes a string by removing all non-digit characters and optionally limiting its length
 * @param {string} value - The input string to be sanitized
 * @param {number} [maxLen] - Optional maximum length for the sanitized string
 * @returns {string} A string containing only digits, optionally truncated to maxLen characters
 * @example
 * sanitizeDigits('abc123def456', 5); // returns '12345'
 * sanitizeDigits('phone: 555-1234'); // returns '5551234'
 */
function sanitizeDigits(value, maxLen) {
    var sanitized = value.replace(/[^0-9]/g, '');
    if (typeof maxLen === 'number') {
        sanitized = sanitized.substring(0, maxLen);
    }
    return sanitized;
}

/**
 * Normalizes first/last name inputs by limiting to letters/spaces and collapsing whitespace.
 *
 * @param {string} value - Raw input string.
 * @returns {string} Sanitized name string.
 */
function sanitizeName(value) {
    var name = (value || '').toString();
    name = name.replace(/^\s+/, '');
    name = name.replace(/[^a-zA-Z\s]/g, '');
    name = name.replace(/\s{2,}/g, ' ');
    return name;
}

/**
 * Sanitizes a city name input string by normalizing whitespace and restricting
 * characters to alphabetic letters and spaces.
 *
 * Processing rules:
 * - Converts the input to a string (treats null/undefined as an empty string)
 * - Removes leading whitespace
 * - Removes any non-letter characters (only A–Z/a–z and spaces are kept)
 * - Collapses consecutive spaces into a single space
 *
 * @param {*} value - The raw city input value to sanitize.
 * @returns {string} The sanitized city string containing only letters and single spaces.
 */
function sanitizeCity(value) {
    var city = (value || '').toString();

    // Prevent leading spaces
    city = city.replace(/^\s+/, '');

    // Allow only letters and spaces
    city = city.replace(/[^a-zA-Z\s]/g, '');

    // Prevent consecutive spaces
    city = city.replace(/\s{2,}/g, ' ');

    return city;
}

/**
 * Validates an email address string against basic formatting rules.
 *
 * Rules enforced:
 * - Input is coerced to a string and trimmed.
 * - Empty/blank values are invalid.
 * - The local-part (before "@") must contain at least one alphabetic character (A–Z or a–z).
 * - The address must match a strict pattern: `local@domain.tld`
 *   - Local-part allows: letters, digits, `. _ % + -`
 *   - Domain allows: letters, digits, and `-`
 *   - Requires a single dot and a TLD of at least 2 letters (prevents cases like `gmail.com.com`)
 *
 * @param {*} value - The value to validate as an email address.
 * @returns {boolean} `true` if the value is a valid email per the rules above; otherwise `false`.
 */
function validateEmail(value) {
    var email = (value || '').toString().trim();
    if (!email) {
        return false;
    }

    // strict email validation (prevents gmail.com.com)
    return /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+\.[A-Za-z]{2,}$/.test(email);
}

/**
 * Validates whether a phone number matches the selected country's rules.
 *
 * The value is considered valid when it:
 * - Is exactly 10 digits long
 * - Contains only numeric characters (no spaces, prefixes, or separators)
 * - Starts with digits 6–9 if the active country code is +91
 *
 * @param {string} value - The phone number string to validate.
 * @param {string} [countryCode='+91'] - Selected country code.
 * @returns {boolean} `true` if the value matches the expected format; otherwise `false`.
 */
function validatePhone(value, countryCode) {
    const maxLength = $('#mobile-number').attr('maxlength');

    // Check exact digit length dynamically
    if (!new RegExp(`^[0-9]{${maxLength}}$`).test(value)) {
        return false;
    }

    // Default country +91
    if ((countryCode || '+91') === '+91') {
        // First digit 6-9 and remaining digits dynamic
        return new RegExp(`^[6-9][0-9]{${maxLength - 1}}$`).test(value);
    }

    return true;
}

/**
 * Resets the resume/file upload section to its initial state.
 *
 * - Clears the file input value
 * - Shows the upload area
 * - Hides the uploaded file preview
 * - Hides invalid file format and size error messages
 * - Clears the uploaded file name text
 */
function resetResumeUploadSection() {
    var $uploadSection = $('.franchisepage-file-upload');
    $uploadSection.find('#franchisepage-file').val('');
    $uploadSection.find('.ckc-upload-area').removeClass('d-none');
    $uploadSection.find('.ckc-uploaded-file').addClass('d-none');
    $uploadSection.find('.invalid-file-format, .invalid-file-size').addClass('d-none');
    $uploadSection.find('.ckc-uploaded-file-name').text('');
}

/**
 * Resets the country code dropdown to a default value.
 *
 * - If a mobileDialingCode is provided, it selects that specific country code.
 * - If not provided, it selects the first available option in the dropdown.
 * - Updates the UI selection state and hides the dropdown.
 * - Makes the dropdown non-editable if a dialing code is explicitly passed.
 *
 * @function resetCountryCodeToDefault
 * @param {string} [mobileDialingCode] - Optional country dialing code (e.g., "+91").
 * @returns {void}
 */
function resetCountryCodeToDefault(mobileDialingCode) {
    var franchiseFormCountryCode = mobileDialingCode || ''; // Default to empty string
    var $container = $('.franchisepage-customer-phone');
    var $defaultOption;

    // If countryCode is passed → use it
    if (franchiseFormCountryCode) {
        $defaultOption = $container
            .find('.custom-option-value[data-id="' + franchiseFormCountryCode + '"]')
            .first();
    } else { // Else → pick FIRST option from the list
        $defaultOption = $container.find('.custom-option-value').first();
    }

    if ($defaultOption.length) {
        var defaultCode = $defaultOption.data('id');

        // Update display
        $container.find('.selected-value, .country-selected-value')
            .text(defaultCode)
            .addClass('active');

        // Reset selected states
        $container.find('.custom-option-value, .country-option-item')
            .removeClass('selected');

        $defaultOption.addClass('selected');
        $container.addClass('selected');

        // Hide dropdown
        $container.find('.custom-option-values, .country-dropdown-options').hide();
    }

    if (mobileDialingCode) {
        var phoneLength = $('#franchisepage-form .custom-option-value.custom-option-login.selected').data('phonelength');
        $('#franchisepage-form #mobile-number').attr('maxlength', phoneLength);
        $container.find('.custom-drop-down-section').addClass('non-editable');
    }
}

/**
 * Returns the currently selected country code, defaulting to +91.
 *
 * @returns {string} The selected country code.
 */
function getSelectedCountryCode() {
    var $code = $('.franchisepage-customer-phone #select-country-code');
    var code = ($code.text() || '').trim();
    return code || '+91';
}

/**
 * Resets the customer title dropdown to a default or specified value.
 *
 * - If a customerTitle is provided, it selects the matching option.
 * - If not provided or no match found, it selects the first available option.
 * - Updates the visible selected value and the hidden input field.
 * - Makes the dropdown non-editable if a title is explicitly passed.
 *
 * @function resetCustomerTitle
 * @param {string} [customerTitle] - Optional customer title (e.g., "Mr", "Ms").
 * @returns {void}
 */
function resetCustomerTitle(customerTitle) {
    var franchiseFormCustomerTitle = customerTitle || ''; // Default to empty string
    var $nameSection = $('.franchisepage-form-name-section');
    if (!$nameSection.length) return;

    var $dropdown = $nameSection.find('.custom-drop-down-section');
    if (!$dropdown.length) return;

    var $options = $dropdown.find('.custom-option-value');
    if (!$options.length) return;

    var $display = $dropdown.find('.selected-value').first();
    var $hiddenInput = $nameSection.find('input[name="customerTitle"]');

    var $targetOption;

    // If customerTitle is passed → select matching option
    if (franchiseFormCustomerTitle) {
        $targetOption = $options.filter(function () {
            var title = $(this).attr('data-title') || $(this).text().trim();
            return title === franchiseFormCustomerTitle;
        }).first();
    }

    // If NOT passed or not found → select FIRST <li>
    if (!$targetOption || !$targetOption.length) {
        $targetOption = $options.first();
    }

    var nextTitle = $targetOption.attr('data-title') || $targetOption.text().trim();
    var nextCode = $targetOption.attr('data-code');

    // Reset selection
    $options.removeClass('selected');
    $targetOption.addClass('selected');

    // Update visible text
    if ($display.length) {
        $display.text(nextTitle);
    }

    // Update hidden field
    if ($hiddenInput.length && nextCode) {
        $hiddenInput.val(nextCode);
    }

    if (customerTitle) {
        $dropdown.addClass('non-editable');
    }
}

/**
 * Retrieves feedback elements related to a given form input.
 *
 * Finds the closest form group and returns the associated
 * invalid feedback and custom feedback elements.
 *
 * @param {jQuery} $input - The input element wrapped in a jQuery object.
 * @returns {Object} An object containing feedback elements.
 * @returns {jQuery} returns.$invalidFeedback - The invalid feedback element.
 * @returns {jQuery} returns.$customFeedback - The custom feedback element.
 */
function getFeedbackElements($input) {
    var $group = $input.closest('.form-group');
    return {
        $invalidFeedback: $group.find('.invalid-feedback').first(),
        $customFeedback: $group.find('.custom-feedback').first()
    };
}

/**
 * Displays the missing/required field error for a form input.
 *
 * - Marks the input as invalid
 * - Shows the default invalid feedback message
 * - Hides any custom feedback message
 *
 * @param {jQuery} $input - The input element wrapped in a jQuery object.
 */
function showMissingError($input) {
    var feedback = getFeedbackElements($input);

    $input.addClass('is-invalid');
    feedback.$invalidFeedback.removeClass('d-none');
    feedback.$customFeedback.addClass('d-none').removeClass('d-block');

    if ($input.hasClass('input-mobile')) {
        $('.franchisepage-customer-phone .custom-drop-down-section').addClass('is-invalid');
        $('.phone-missing-error').removeClass('d-none').addClass('d-block');
    }

    if ($input.hasClass('first-name-input')) {
        $('.franchisepage-form-name-section .custom-drop-down-section').addClass('is-invalid');
    }

    if ($input.hasClass('email-input')) {
        $('.email-missing-error').removeClass('d-none').addClass('d-block');
    }
}

/**
 * Displays a custom validation error message for a form input.
 *
 * - Marks the input as invalid
 * - Hides the default invalid feedback message
 * - Shows the custom feedback message
 *
 * @param {jQuery} $input - The input element wrapped in a jQuery object.
 */
function showCustomError($input) {
    var feedback = getFeedbackElements($input);

    $input.addClass('is-invalid');
    feedback.$invalidFeedback.addClass('d-none').removeClass('d-block');
    feedback.$customFeedback.removeClass('d-none').addClass('d-block');

    if ($input.hasClass('input-mobile')) {
        $('.franchisepage-customer-phone .custom-drop-down-section').addClass('is-invalid');
    }
}

/**
 * Clears validation error styling and toggles feedback elements for the given input.
 *
 * Removes the `is-invalid` class from the input and updates the associated feedback
 * elements so that the invalid feedback is shown and any custom feedback is hidden.
 *
 * @function clearErrors
 * @param {JQuery<HTMLElement>} $input - jQuery-wrapped input element whose error state should be cleared.
 * @returns {void}
 */
function clearErrors($input) {
    var feedback = getFeedbackElements($input);

    $input.removeClass('is-invalid');
    feedback.$invalidFeedback.removeClass('d-none');
    feedback.$customFeedback.addClass('d-none').removeClass('d-block');

    if ($input.hasClass('input-mobile')) {
        $('.franchisepage-customer-phone .custom-drop-down-section').removeClass('is-invalid');
    } else if ($input.hasClass('first-name-input')) {
        $('.franchisepage-form-name-section .custom-drop-down-section').removeClass('is-invalid');
    } else if ($input.hasClass('email-input')) {
        $('.email-missing-error').addClass('d-none').removeClass('d-block');
    }
}

/**
 * Validates the current value of a jQuery input element and toggles UI feedback (error messages)
 * based on missing/invalid input rules.
 *
 * Rules:
 * - Trims the input value; if empty, shows a "missing" error and fails validation.
 * - If the input has `.email-input`, validates as email; on failure shows a custom error.
 * - If the input has `.input-mobile`, validates as a mobile phone; on failure shows a custom error.
 * - If the input has `.landline-input`, validates as a landline; on failure shows a custom error.
 * - On successful validation for the applicable type, clears any existing errors.
 * - Inputs with none of the above type classes pass validation as long as they are non-empty.
 *
 * Side effects:
 * - Calls `showMissingError`, `showCustomError`, and `clearErrors` to update UI state.
 *
 * @param {JQuery} $input - jQuery-wrapped input element whose value and classes determine validation behavior.
 * @returns {boolean} `true` if the input is non-empty and (when applicable) passes type-specific validation; otherwise `false`.
 */
function validateAndToggleFeedback($input) {
    var rawValue = ($input.val() || '').toString();
    var value = rawValue.trim();

    if (value === '') {
        showMissingError($input);
        return false;
    }

    if ($input.hasClass('first-name-input') || $input.hasClass('last-name-input')) {
        var sanitizedName = sanitizeName(value);
        if (sanitizedName !== value) {
            value = sanitizedName;
        }
        value = value.trim();
        $input.val(value);

        if (value === '') {
            showMissingError($input);
            return false;
        }

        clearErrors($input);
        return true;
    }

    if ($input.hasClass('city-input')) {
        var sanitizedCityValue = sanitizeCity(value);
        if (sanitizedCityValue !== value) {
            value = sanitizedCityValue;
        }
        value = value.trim();
        $input.val(value);

        if (value === '') {
            showMissingError($input);
            return false;
        }

        clearErrors($input);
        return true;
    }

    if ($input.hasClass('email-input')) {
        if (!validateEmail(value)) {
            showCustomError($input);
            return false;
        }
        clearErrors($input);
        return true;
    }

    if ($input.hasClass('input-mobile')) {
        var countryCode = getSelectedCountryCode();
        var sanitizedValue = sanitizeMobile(value, countryCode);
        if (sanitizedValue !== value) {
            value = sanitizedValue;
            $input.val(value);
        }

        if (!validatePhone(value, countryCode)) {
            showCustomError($input);
            $('.franchisepage-customer-phone .custom-drop-down-section').addClass('is-invalid');
            return false;
        }
        clearErrors($input);

        return true;
    }

    return true;
}

$(document).ready(function () {
    var $container = $('#staticfranchisepage-form-container');
    var $url = $container.data('url');
    if ($url) {
        $.ajax({
            url: $url,
            method: 'GET',
            success: function (data) {
                $container.html(data);
                var customerTitle = $('.franchisepage-customer-title').val();
                var mobileDialingCode = $('.franchisepage-mobiledialingcode').val();
                if (customerTitle) resetCustomerTitle(customerTitle);
                if (mobileDialingCode) resetCountryCodeToDefault(mobileDialingCode);
            }
        });
    }

    // Input sanitization (delegated: form is injected via AJAX)
    $(document).on('input', '#franchisepage-form .franchisepage-form-input', function () {
        var $input = $(this);

        // First/Last name sanitization
        if ($input.hasClass('first-name-input') || $input.hasClass('last-name-input')) {
            var nameVal = $input.val();

            // Prevent first space
            if (nameVal.startsWith(' ')) {
                $input.val('');
                showMissingError($input);
                return;
            }

            // Allow only letters and spaces
            nameVal = nameVal.replace(/[^a-zA-Z\s]/g, '');

            // Prevent consecutive spaces
            if (/\s{2,}$/.test(nameVal)) {
                nameVal = nameVal.slice(0, -1);
            }

            $input.val(nameVal);

            // Show error when input is empty
            if (nameVal.trim() === '') {
                $input.val('');
                showMissingError($input);
                return;
            }

            clearErrors($input);
        }

        // City sanitization: letters + spaces only
        if ($input.hasClass('city-input')) {
            var cityVal = $input.val();

            // Prevent first space
            if (cityVal && cityVal.startsWith(' ')) {
                $input.val('');
                showMissingError($input);
                return;
            }

            cityVal = sanitizeCity(cityVal);
            $input.val(cityVal);

            // Show error when input is empty
            if (cityVal.trim() === '') {
                $input.val('');
                showMissingError($input);
                return;
            }

            clearErrors($input);
        }

        // Only sanitize these fields; do not change validation for others
        if ($input.hasClass('email-input')) {
            if ($input.val().trim() === '') {
                $input.addClass('is-invalid');
                var missingError = $input.data('missing-error');
                var $missingErrorElem = $('.email-missing-error');
                $missingErrorElem.text(missingError);
                $missingErrorElem.removeClass('d-none').addClass('d-block');
                $missingErrorElem.siblings('.custom-feedback').addClass('d-none').removeClass('d-block');
                return;
            }
            $input.val(sanitizeEmail($input.val()));
        }

        if ($input.hasClass('input-mobile')) {
            if ($input.val().trim() === '') {
                $input.addClass('is-invalid');
                $('.franchisepage-customer-phone .custom-drop-down-section').addClass('is-invalid');
                $('.phone-missing-error').removeClass('d-none').addClass('d-block');
                $('.phone-custom-error').addClass('d-none').removeClass('d-block');
                return;
            }

            var countryCode = getSelectedCountryCode();
            $input.val(sanitizeMobile($input.val(), countryCode));

            if ($input.val().trim() !== '') {
                $('.input-mobile, .franchisepage-customer-phone .custom-drop-down-section').removeClass('is-invalid');
                $('.phone-missing-error').removeClass('d-block');
            } else {
                showCustomError($input);
            }
        }

        if ($input.hasClass('landline-input')) {
            $input.val(sanitizeDigits($input.val(), 15));
        }

        // Match other forms: once user starts typing, hide feedback until blur/submit
        if ($input.hasClass('email-input') || $input.hasClass('input-mobile') || $input.hasClass('landline-input')) {
            if (($input.val() || '').toString().trim() !== '') {
                clearErrors($input);
            }
        }
    });

    // Blur validation
    $(document).on('blur', '#franchisepage-form .email-input, #franchisepage-form .input-mobile, #franchisepage-form .first-name-input, #franchisepage-form .last-name-input, #franchisepage-form .city-input', function () {
        validateAndToggleFeedback($(this));
    });

    // Choose file button should open the hidden file input
    $(document).on('click', '#franchisepage-form #choose-file-btn', function (e) {
        e.preventDefault();

        var $wrap = $(this).closest('.franchisepage-file-upload');
        $wrap.find('.franchisepage-file-input').trigger('click');
    });

    // Handle file selection
    $(document).on('change', '.franchisepage-file-input', function () {
        var $wrap = $(this).closest('.franchisepage-file-upload');
        var fileInput = this;
        var file = fileInput.files[0];

        var uploadArea = $wrap.find('.ckc-upload-area');
        var uploadedFile = $wrap.find('.ckc-uploaded-file');
        var fileNameText = $wrap.find('.ckc-uploaded-file-name');

        var imgPreview = $wrap.find('.file-image-preview');
        var pdfThumb = $wrap.find('.pdf-thumb');

        var invalidFormat = $wrap.find('.invalid-file-format');
        var invalidSize = $wrap.find('.invalid-file-size');

        // reset UI
        invalidFormat.addClass('d-none');
        invalidSize.addClass('d-none');
        imgPreview.addClass('d-none').attr('src', '');
        pdfThumb.addClass('d-none');

        if (!file) return;

        var MAX_SIZE = 5 * 1024 * 1024;

        var allowedTypes = [
            'image/jpeg',
            'image/png',
            'image/webp',
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/zip'
        ];

        if (!allowedTypes.includes(file.type)) {
            invalidFormat.removeClass('d-none');
            fileInput.value = '';
            return;
        }

        if (file.size > MAX_SIZE) {
            invalidSize.removeClass('d-none');
            fileInput.value = '';
            return;
        }

        fileNameText.text(file.name);
        uploadArea.addClass('d-none');
        uploadedFile.removeClass('d-none');

        // PREVIEW RULE
        if (file.type.startsWith('image/')) {
            // IMAGE → actual preview
            var reader = new FileReader();
            reader.onload = function (e) {
                imgPreview.attr('src', e.target.result).removeClass('d-none');
            };
            reader.readAsDataURL(file);
        } else {
            // EVERYTHING ELSE → pdf-thumb SVG
            pdfThumb.removeClass('d-none');
        }
    });

    // Remove file
    $(document).on('click', '.ckc-remove-file-btn', function () {
        var $wrap = $(this).closest('.franchisepage-file-upload');

        $wrap.find('.franchisepage-file-input').val('');
        $wrap.find('.ckc-upload-area').removeClass('d-none');
        $wrap.find('.ckc-uploaded-file').addClass('d-none');

        $wrap.find('.file-image-preview').addClass('d-none').attr('src', '');
        $wrap.find('.pdf-thumb').addClass('d-none');

        $wrap.find('.invalid-file-format, .invalid-file-size').addClass('d-none');
        $wrap.find('.ckc-uploaded-file-name').text('');
    });

    // Clear mobile number on option select
    $(document).on('click', '.franchisepage-customer-phone .custom-option-value ', function () {
        $('#mobile-number').val('').removeClass('is-invalid');
        $('.phone-missing-error, .phone-custom-error').addClass('d-none').removeClass('d-block');
        $('.franchisepage-customer-phone .custom-drop-down-section').removeClass('is-invalid');

        var phoneLength = $(this).data('phonelength');
        $('#mobile-number').attr('maxlength', phoneLength);
    });

    // Submit validation: ensure feedback appears on submit too
    $(document).on('submit', '#franchisepage-form', function (e) {
        e.preventDefault();

        var $this = $(this);
        var url = $this.data('url');
        var customerTitle = $('.franchisepage-form-name-section  .selected-value').text();
        var fname = $('#first-name').val().trim();
        var lname = $('#last-name').val().trim();
        var email = ($('#email').val() || '').toString();
        var city = ($('#city').val() || '').toString().trim();
        var landline = ($('#landlinenumber').val() || '').toString().trim();
        var countryCode = getSelectedCountryCode();
        var phone = ($('#mobile-number').val() || '').toString().trim();
        phone = sanitizeMobile(phone, countryCode);
        $('#mobile-number').val(phone);
        var fileInput = $('#franchisepage-file')[0];
        var file = fileInput.files[0];

        var hasError = false;

        if (!fname) {
            showMissingError($('#first-name'));
            $('.franchisepage-form-name-section .custom-drop-down-section').addClass('is-invalid');
            hasError = true;
        }

        if (!lname) {
            showMissingError($('#last-name'));
            hasError = true;
        }

        if (!city) {
            showMissingError($('#city'));
            hasError = true;
        }

        if (!phone || !validatePhone(phone, countryCode)) {
            if (!phone) {
                showMissingError($('#mobile-number'));
            } else {
                showCustomError($('#mobile-number'));
            }
            hasError = true;
            $('.phone-missing-error').removeClass('d-none').addClass('d-block');
            $('.phone-custom-error').removeClass('d-block').addClass('d-none');
            $('.franchisepage-customer-phone .custom-drop-down-section').addClass('is-invalid');
        }

        email = sanitizeEmail(email);
        $('#email').val(email);
        if (!email || !validateEmail(email)) {
            if (!email) {
                showMissingError($('#email'));
            } else {
                showCustomError($('#email'));
            }
            hasError = true;
        }

        if (!file) {
            $('.invalid-file-format').removeClass('d-none');
            $('.invalid-file-size').addClass('d-none');
            hasError = true;
        }

        if (hasError) return;

        var formData = new FormData();
        formData.append('customerTitle', customerTitle);
        formData.append('fname', fname);
        formData.append('lname', lname);
        formData.append('city', city);
        formData.append('landline', landline);
        formData.append('countryCode', countryCode);
        formData.append('phone', phone);
        formData.append('email', email);
        formData.append('resumeFile', file);
        $.spinner().start();
        $.ajax({
            url: url,
            type: 'post',
            dataType: 'json',
            data: formData,
            processData: false,
            contentType: false,
            success: function (data) {
                if (data.success) {
                    $('#franchisepagesubmitresponse').modal('show');
                } else {
                    $('#franchisepagealreadyresponse .message-text').text(data.message);
                    $('#franchisepagealreadyresponse').modal('show');
                }
                $('#franchisepage-form')[0].reset();
                resetResumeUploadSection();
                resetCountryCodeToDefault();
                resetCustomerTitle();
                $.spinner().stop();
            },
            error: function () {
                $.spinner().stop();
            }
        });
    });
});
